import { events } from '../constants';
import { sendDataToGA4 } from './ga4'; // Adjust import path as needed

const { EVENT_NAMES, EVENT_CATEGORIES, EVENT_ACTIONS } = events;

/**
 * Helper function to create event objects
 * @param {string} eventName - The GA4 event name
 * @param {object} data - Event data object
 * @returns {object} Event configuration object
 */
function createEvent(event, data) {
  return {
    event,
    data
  };
}

// Event configuration mapping
const eventConfigs = {
  // Header Navigation Events
  HeaderNavigationCountryClick: {
    event: EVENT_NAMES.HEADER_STOREFRONT_NAVIGATION,
    eventCategory: EVENT_CATEGORIES.HEADER_STORE_NAVIGATION,
    eventAction: EVENT_ACTIONS.COUNTRY_DROPDOWN_CLICK,
    scenario: 'Header country dropdown clicked'
  },
  HeaderNavigationCountrySelection: {
    event: EVENT_NAMES.HEADER_STOREFRONT_NAVIGATION,
    eventCategory: EVENT_CATEGORIES.HEADER_STORE_NAVIGATION,
    eventAction: EVENT_ACTIONS.COUNTRY_SELECTION,
    scenario: 'Header country selected'
  },
  
  // Footer Navigation Events
  FooterNavigationBar: {
    event: EVENT_NAMES.FOOTER_NAVIGATION_BAR,
    eventAction: EVENT_ACTIONS.LINK_CLICK,
    scenario: 'Footer navigation link clicked'
  },
  FooterStorefrontNavigation: {
    event: EVENT_NAMES.FOOTER_STOREFRONT_NAVIGATION,
    eventCategory: EVENT_CATEGORIES.FOOTER_STOREFRONT_NAVIGATION,
    eventAction: EVENT_ACTIONS.COUNTRY_DROPDOWN_CLICK,
    scenario: 'Footer country dropdown clicked'
  },
  FooterStorefrontNavigationCountryClick: {
    event: EVENT_NAMES.FOOTER_STOREFRONT_NAVIGATION,
    eventCategory: EVENT_CATEGORIES.FOOTER_STOREFRONT_NAVIGATION,
    eventAction: EVENT_ACTIONS.COUNTRY_CLICK,
    scenario: 'Footer country clicked'
  },
  
  // Header Menu Events
  HeaderMenu: {
    event: EVENT_NAMES.HEADER_MENU,
    eventCategory: EVENT_CATEGORIES.HEADER_MENU,
    eventAction: EVENT_ACTIONS.CLICK,
    scenario: 'Header menu clicked'
  },
  HeaderSubmenuClick: {
    event: EVENT_NAMES.HEADER_SUBMENU,
    eventAction: EVENT_ACTIONS.CLICK,
    scenario: 'Header submenu clicked'
  },
  HeaderSubmenuAppClick: {
    event: EVENT_NAMES.HEADER_SUBMENU,
    eventAction: EVENT_ACTIONS.APP_ID_CLICK,
    scenario: 'Header submenu app clicked'
  },
  HeaderSubmenuCategoryCtaClick: {
    event: EVENT_NAMES.HEADER_SUBMENU,
    eventAction: EVENT_ACTIONS.CATEGORY_CTA_CLICK,
    scenario: 'Header submenu category CTA clicked'
  },
  HeaderSubmenuAppReviewsClick: {
    event: EVENT_NAMES.HEADER_SUBMENU,
    eventAction: EVENT_ACTIONS.APP_REVIEWS_CLICK,
    scenario: 'Header submenu app reviews clicked'
  },
  HeaderUserNameClick: {
    event: EVENT_NAMES.HEADER_USERNAME_CLICK,
    eventCategory: EVENT_CATEGORIES.HEADER_USERNAME_CLICK,
    eventAction: EVENT_ACTIONS.CLICK,
    scenario: 'Header username clicked'
  },
  
  // Search Events
  HeaderSearchInitiate: {
    event: EVENT_NAMES.HEADER_SEARCH,
    eventCategory: EVENT_CATEGORIES.HEADER_SEARCH,
    eventAction: EVENT_ACTIONS.INITIATE,
    eventLabel: EVENT_ACTIONS.INITIATE,
  },
  HeaderSearchComplete: {
    event: EVENT_NAMES.HEADER_SEARCH,
    eventCategory: EVENT_CATEGORIES.HEADER_SEARCH,
    eventAction: EVENT_ACTIONS.COMPLETE,
  },
  
  // Contact Us Events
  HeaderContactUsTrigger: {
    event: EVENT_NAMES.HEADER_CONTACT_US,
    eventCategory: EVENT_CATEGORIES.HEADER_CONTACT_US,
    eventAction: EVENT_ACTIONS.TRIGGER_CTA_CLICK,
    scenario: 'Header contact us triggered'
  },
  HeaderContactUsFormFill: {
    event: EVENT_NAMES.HEADER_CONTACT_US,
    eventCategory: EVENT_CATEGORIES.HEADER_CONTACT_US,
    scenario: 'Header contact us form filled'
  },
  HeaderContactUsSubmit: {
    event: EVENT_NAMES.HEADER_CONTACT_US,
    eventCategory: EVENT_CATEGORIES.HEADER_CONTACT_US,
    eventAction: EVENT_ACTIONS.SUBMIT_CLICK,
    eventLabel: EVENT_ACTIONS.SUBMIT_CLICK,
    scenario: 'Header contact us submitted'
  },
  HeaderContactUsCancel: {
    event: EVENT_NAMES.HEADER_CONTACT_US,
    eventCategory: EVENT_CATEGORIES.HEADER_CONTACT_US,
    eventAction: EVENT_ACTIONS.CANCEL_CLICK,
    eventLabel: EVENT_ACTIONS.CANCEL_CLICK,
    scenario: 'Header contact us cancelled'
  },
  HeaderContactUsSubmissionSuccess: {
    event: EVENT_NAMES.HEADER_CONTACT_US,
    eventCategory: EVENT_CATEGORIES.HEADER_CONTACT_US,
    eventAction: EVENT_ACTIONS.SUBMISSION_SUCCESS,
    eventLabel: EVENT_ACTIONS.SUBMISSION_SUCCESS,
    scenario: 'Header contact us submission success'
  },
  HeaderContactUsSubmissionFail: {
    event: EVENT_NAMES.HEADER_CONTACT_US,
    eventCategory: EVENT_CATEGORIES.HEADER_CONTACT_US,
    eventAction: EVENT_ACTIONS.SUBMISSION_FAIL,
    eventLabel: EVENT_ACTIONS.SUBMISSION_FAIL,
    scenario: 'Header contact us submission failed'
  },
  // Logo Events
  AdpLogoClick: {
    event: EVENT_NAMES.ADP_LOGO,
    eventCategory: EVENT_CATEGORIES.ADP_LOGO,
    eventAction: EVENT_ACTIONS.CLICK,
    eventLabel: EVENT_ACTIONS.CLICK,
    scenario: 'ADP logo clicked'
  },
  
  // Login Events
  HeaderLoginInitiate: {
    event: EVENT_NAMES.HEADER_LOGIN,
    eventCategory: EVENT_CATEGORIES.LOGIN_CTA,
    eventAction: EVENT_ACTIONS.INITIATE,
    eventLabel: EVENT_ACTIONS.INITIATE,
    scenario: 'Header login initiated'
  },
  HeaderLoginComplete: {
    event: EVENT_NAMES.HEADER_LOGIN,
    eventCategory: EVENT_CATEGORIES.LOGIN_CTA,
    eventAction: EVENT_ACTIONS.COMPLETE,
    scenario: 'Header login completed'
  },
  
  // Cart Events
  HeaderCartDialogueView: {
    event: EVENT_NAMES.HEADER_CART,
    eventCategory: EVENT_CATEGORIES.HEADER_CART,
    eventAction: EVENT_ACTIONS.DIALOGUE_VIEW,
    eventLabel: 'view',
    scenario: 'Header cart dialogue viewed'
  },
  HeaderCartClick: {
    event: EVENT_NAMES.HEADER_CART,
    eventCategory: EVENT_CATEGORIES.HEADER_CART,
    eventAction: EVENT_ACTIONS.CART_CLICK,
    eventLabel: EVENT_ACTIONS.CLICK,
    scenario: 'Header cart clicked'
  },
  HeaderCartDialogueClick: {
    event: EVENT_NAMES.HEADER_CART,
    eventCategory: EVENT_CATEGORIES.HEADER_CART,
    eventAction: EVENT_ACTIONS.CART_DIALOGUE_CLICK,
    scenario: 'Header cart dialogue clicked'
  },
  
  // Scroll Events
  Scroll25: {
    event: EVENT_NAMES.SCROLL_25,
    eventLabel: '25',
    scenario: 'Page scrolled 25%'
  },
  Scroll50: {
    event: EVENT_NAMES.SCROLL_50,
    eventLabel: '50',
    scenario: 'Page scrolled 50%'
  },
  Scroll75: {
    event: EVENT_NAMES.SCROLL_75,
    eventLabel: '75',
    scenario: 'Page scrolled 75%'
  },
  Scroll90: {
    event: EVENT_NAMES.SCROLL_90,
    eventLabel: '90',
    scenario: 'Page scrolled 90%'
  },
  
  // Smart Search Events
  HeaderSmartSearchInitiate: {
    event: EVENT_NAMES.HEADER_SMART_SEARCH,
    eventCategory: EVENT_CATEGORIES.HEADER_SMART_SEARCH,
    eventAction: EVENT_ACTIONS.INITIATE,
    scenario: 'Header smart search initiated'
  },

  HeaderSmartSearchClose: {
    event: EVENT_NAMES.HEADER_SMART_SEARCH,
    eventCategory: EVENT_CATEGORIES.HEADER_SMART_SEARCH,
    eventAction: EVENT_ACTIONS.CLOSE,
    scenario: 'Header smart search closed'
  },
  
  // About Events
  HeaderAboutClick: {
    event: EVENT_NAMES.HEADER_ABOUT,
    eventCategory: EVENT_CATEGORIES.HEADER_ABOUT,
    eventAction: EVENT_ACTIONS.CLICK,
    scenario: 'Header about clicked'
  },
  HeaderAISolutionsClick: {
    event: EVENT_NAMES.HEADER_AI_SOLUTIONS,
    eventCategory: EVENT_CATEGORIES.HEADER_AI_SOLUTIONS,
    eventAction: EVENT_ACTIONS.CLICK,
    scenario: 'Header AI Solutions clicked'
  },

  // My Apps Events
  MyAppsMenuClick: {
    event: EVENT_NAMES.MYAPPS_MENU_CLICK,
    eventCategory: EVENT_CATEGORIES.MYAPPS_NAVIGATION,
    eventAction: EVENT_ACTIONS.CLICK,
    eventLabel: 'myapps_menu_click',
    scenario: 'User clicks on My Apps from the user dropdown menu'
  },
  // Footer Legal Links
  FooterLegalLinksClick: {
    event: EVENT_NAMES.FOOTER_LEGAL_LINKS_CLICK,
    eventCategory: EVENT_CATEGORIES.FOOTER_LEGAL_LINKS_CLICK,
    eventAction: EVENT_ACTIONS.CLICK,
    scenario: 'Footer legal links clicked'
  }
};

// Generate and export all events dynamically
const generatedEvents = Object.entries(eventConfigs).reduce((acc, [key, config]) => {
  const { event, ...data } = config;
  acc[key] = createEvent(event, data);
  return acc;
}, {});

// Export all events
export const {
  HeaderNavigationCountryClick,
  HeaderNavigationCountrySelection,
  FooterNavigationBar,
  FooterStorefrontNavigation,
  FooterStorefrontNavigationCountryClick,
  FooterLegalLinksClick,
  HeaderMenu,
  HeaderSubmenuClick,
  HeaderSubmenuAppClick,
  HeaderSubmenuCategoryCtaClick,
  HeaderSubmenuAppReviewsClick,
  HeaderSearchInitiate,
  HeaderSearchComplete,
  HeaderContactUsTrigger,
  HeaderContactUsFormFill,
  HeaderContactUsSubmit,
  HeaderContactUsCancel,
  HeaderContactUsSubmissionSuccess,
  HeaderContactUsSubmissionFail,
  AdpLogoClick,
  HeaderLoginInitiate,
  HeaderLoginComplete,
  HeaderCartDialogueView,
  HeaderCartClick,
  HeaderCartDialogueClick,
  HeaderSmartSearchClose,
  Scroll25,
  Scroll50,
  HeaderUserNameClick,
  Scroll75,
  Scroll90,
  HeaderSmartSearchInitiate,
  HeaderAboutClick,
  HeaderAISolutionsClick,
  MyAppsMenuClick
} = generatedEvents;


/**
 * Sends GA4 event data with merged configuration
 * @param {object} eventInfo - Event configuration object from generatedEvents
 * @param {object} customData - Additional/override event data
 */
export function sendGA4Data(eventInfo, customData = {}) {
  if (!eventInfo) {
    console.warn('Invalid event info provided to sendGA4Data');
    return;
  }

  // wrapping in try-catch to prevent GA4 errors from breaking the app and navigation
  try {
    const eventName = customData?.customEventName || eventInfo.event;
    const eventData = {
      ...eventInfo.data,
      ...customData
    };

    delete eventData.customEventName;

    sendDataToGA4(eventName, eventData);
  } catch (error) {
    console.warn('Failed to send GA4 event', error);
  }
}